import { useToast } from "@/hooks/use-toast";
import { CampaignFormData, Creative, Location } from "../types";
import { useAdminBypass } from "@/contexts/AdminBypassContext";

export const useStepValidation = () => {
  const { toast } = useToast();
  const { canBypassDateRestrictions } = useAdminBypass();

  // Real-time validation functions (no toast, just return boolean)
  const isInformationStepValid = (formData: CampaignFormData): boolean => {
    if (!formData.companyName || !formData.brandName || !formData.personInCharge || 
        !formData.jobTitle || !formData.email || !formData.mobile) {
      return false;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return false;
    }
    
    // Validate phone number (basic validation)
    const phoneRegex = /^\+?[0-9\s\-()]{8,20}$/;
    if (!phoneRegex.test(formData.mobile)) {
      return false;
    }
    
    return true;
  };

  const isLocationStepValid = (selectedLocations: Location[]): boolean => {
    return selectedLocations.length > 0;
  };

  const isTimeStepValid = (duration: number): boolean => {
    if (duration === 0) {
      return false;
    }
    
    // Admin can bypass date restrictions
    if (canBypassDateRestrictions) {
      return true;
    }
    
    // Check the date selection mode
    const mode = localStorage.getItem('dateSelectionMode');
    
    if (mode === 'specific') {
      // For specific mode, validate that selected dates are in the future
      const selectedDatesStr = localStorage.getItem('selectedDates');
      if (selectedDatesStr) {
        try {
          const dates = JSON.parse(selectedDatesStr).map((d: string) => new Date(d));
          const now = new Date();
          const minDate = new Date(now);
          minDate.setHours(now.getHours() + 48);
          
          // All dates must be at least 48 hours in the future
          const allValid = dates.every((date: Date) => date >= minDate);
          if (!allValid) {
            return false;
          }
        } catch {
          return false;
        }
      }
    } else {
      // For range mode, validate start date
      const startDateStr = localStorage.getItem('startDate');
      if (startDateStr) {
        const startDate = new Date(startDateStr);
        const now = new Date();
        const minDate = new Date(now);
        minDate.setHours(now.getHours() + 48); // 48 hours from now
        
        if (startDate < minDate) {
          return false;
        }
      }
    }
    
    return true;
  };

  const isCreativesStepValid = (
    creatives: Creative[],
    locationCreatives: Record<string, Creative[]>,
    mediaOwnerCreatives: Record<string, Creative[]>,
    useLocationSpecificCreatives: boolean,
    useMediaOwnerSpecificCreatives: boolean,
    campaignName: string
  ): boolean => {
    // Check campaign name first
    if (!campaignName || campaignName.trim() === "") {
      return false;
    }

    // Get all media owners from localStorage
    const savedLocations = localStorage.getItem('selectedLocations');
    const selectedLocations = savedLocations ? JSON.parse(savedLocations) : [];
    
    // Extract unique media owners
    const mediaOwners = [...new Set(
      selectedLocations
        .map((location: Location) => location.mediaOwner)
        .filter(Boolean)
    )];
    
    // Check if all media owners have creatives, excluding "Waki"
    if (mediaOwners.length > 0) {
      const missingCreatives = mediaOwners.filter(
        mediaOwner => {
          // Skip validation for Waki
          if (mediaOwner === "Waki") return false;
          
          const ownerCreatives = mediaOwnerCreatives[mediaOwner as string];
          return !ownerCreatives || ownerCreatives.length === 0;
        }
      );
      
      return missingCreatives.length === 0;
    }
    
    return false;
  };

  const validateInformationStep = (formData: CampaignFormData): boolean => {
    if (!formData.companyName || !formData.brandName || !formData.personInCharge || 
        !formData.jobTitle || !formData.email || !formData.mobile) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return false;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return false;
    }
    
    // Validate phone number (basic validation)
    const phoneRegex = /^\+?[0-9\s\-()]{8,20}$/;
    if (!phoneRegex.test(formData.mobile)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number",
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };

  const validateLocationStep = (selectedLocations: Location[]): boolean => {
    if (selectedLocations.length === 0) {
      toast({
        title: "Location selection required",
        description: "Please select at least one location for your campaign",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const validateTimeStep = (duration: number): boolean => {
    if (duration === 0) {
      toast({
        title: "Campaign duration required",
        description: "Please select a duration for your campaign",
        variant: "destructive",
      });
      return false;
    }
    
    // Admin can bypass date restrictions
    if (canBypassDateRestrictions) {
      return true;
    }
    
    // Check the date selection mode
    const mode = localStorage.getItem('dateSelectionMode');
    
    if (mode === 'specific') {
      // For specific mode, validate that selected dates are in the future
      const selectedDatesStr = localStorage.getItem('selectedDates');
      if (selectedDatesStr) {
        try {
          const dates = JSON.parse(selectedDatesStr).map((d: string) => new Date(d));
          const now = new Date();
          const minDate = new Date(now);
          minDate.setHours(now.getHours() + 48);
          
          const allValid = dates.every((date: Date) => date >= minDate);
          if (!allValid) {
            toast({
              title: "Invalid dates selected",
              description: "All campaign dates must be at least 48 hours from now",
              variant: "destructive",
            });
            return false;
          }
        } catch {
          toast({
            title: "Invalid date selection",
            description: "Please select at least one date for your campaign",
            variant: "destructive",
          });
          return false;
        }
      }
    } else {
      // For range mode, validate start date
      const startDateStr = localStorage.getItem('startDate');
      if (startDateStr) {
        const startDate = new Date(startDateStr);
        const now = new Date();
        const minDate = new Date(now);
        minDate.setHours(now.getHours() + 48); // 48 hours from now
        
        if (startDate < minDate) {
          toast({
            title: "Invalid start date",
            description: "Campaign start date must be at least 48 hours from now",
            variant: "destructive",
          });
          return false;
        }
      }
    }
    
    return true;
  };

  const validateCreativesStep = (
    creatives: Creative[],
    locationCreatives: Record<string, Creative[]>,
    mediaOwnerCreatives: Record<string, Creative[]>,
    useLocationSpecificCreatives: boolean,
    useMediaOwnerSpecificCreatives: boolean
  ): boolean => {
    // Get all media owners from localStorage
    const savedLocations = localStorage.getItem('selectedLocations');
    const selectedLocations = savedLocations ? JSON.parse(savedLocations) : [];
    
    // Extract unique media owners
    const mediaOwners = [...new Set(
      selectedLocations
        .map((location: Location) => location.mediaOwner)
        .filter(Boolean)
    )];
    
    // Check if all media owners have creatives, excluding "Waki"
    if (mediaOwners.length > 0) {
      const missingCreatives = mediaOwners.filter(
        mediaOwner => {
          // Skip validation for Waki
          if (mediaOwner === "Waki") return false;
          
          const ownerCreatives = mediaOwnerCreatives[mediaOwner as string];
          return !ownerCreatives || ownerCreatives.length === 0;
        }
      );
      
      if (missingCreatives.length > 0) {
        toast({
          title: "Missing creative assets",
          description: `Please upload creative assets for all media owners. Missing: ${missingCreatives.join(', ')}`,
          variant: "destructive",
        });
        return false;
      }
      
      return true;
    }
    
    // If no media owners (which shouldn't happen if locations are selected), 
    // fallback to requiring at least one creative
    toast({
      title: "Creative assets required",
      description: "Please upload at least one creative asset for your campaign",
      variant: "destructive",
    });
    return false;
  };

  return {
    validateInformationStep,
    validateLocationStep,
    validateTimeStep,
    validateCreativesStep,
    // Real-time validation functions
    isInformationStepValid,
    isLocationStepValid,
    isTimeStepValid,
    isCreativesStepValid
  };
};
